import React, { useEffect, useState } from 'react';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import RingilCalendar from '@components/framework/input/RingilCalendar.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgRoute from '@components/ringil3/icons/heading/Route.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { PriceIntervalForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import { newPriceInterval, overlappingIntervalKeys } from '@utils/contractedPriceTable.ts';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  table: PriceTableForm;
  subjectName?: string;
  infoText?: string;
  /** Adds an empty period when the sidebar opens (the "Přidat interval" / "Naplánovat" entry points). */
  startWithNewPeriod?: boolean;
  onSave: (intervals: PriceIntervalForm[]) => void;
}

/**
 * "Plán platnosti úrovní": the periods in which each level is valid. Both days are inclusive, periods may leave gaps
 * but must not overlap. Periods already over are kept as they are.
 */
const PriceLevelPlanSidebar: React.FC<ComponentProps> = ({ visible, onHide, table, subjectName, infoText, startWithNewPeriod = false, onSave }) => {
  const { tr } = useTranslationLgs();
  const [intervals, setIntervals] = useState<PriceIntervalForm[]>([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const current = table.intervals.map(i => ({ ...i })).sort((a, b) => (a.validFrom ?? '').localeCompare(b.validFrom ?? ''));
    setIntervals(startWithNewPeriod || current.length === 0 ? [...current, newPriceInterval(table.defaultLevelKey ?? table.levels[0]?.key)] : current);
    setSubmitted(false);
  }, [visible]);

  const overlapping = overlappingIntervalKeys(intervals);
  const rowError = (i: PriceIntervalForm): string | null => {
    if (!i.levelKey || !i.validFrom || !i.validTo) return tr('PricePlan.incomplete', 'Každé období musí mít úroveň a obě data');
    if (i.validFrom > i.validTo) return tr('PricePlan.fromAfterTo', 'Období musí začínat dříve, než končí');
    if (overlapping.has(i.key)) return tr('PricePlan.overlap', 'Období se nesmí překrývat');
    return null;
  };
  const hasErrors = intervals.some(i => rowError(i) !== null);

  const update = (key: string, patch: Partial<PriceIntervalForm>) => setIntervals(list => list.map(i => (i.key === key ? { ...i, ...patch } : i)));

  const save = () => {
    setSubmitted(true);
    if (hasErrors) return;
    onSave(intervals);
    onHide();
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={tr('PricePlan.title', 'Plán platnosti úrovní')}
      onBack={onHide}
      onContinue={save}
      continueLabel={tr('PricePlan.save', 'Uložit')}
      size={'sm'}
      dataTestIdContinue={'price-plan-save'}
    >
      <div className={'flex flex-col gap-4'}>
        {subjectName && (
          <div className={'flex flex-row items-start gap-3 border-b border-solid border-content-quaternary pb-4'}>
            <SvgRoute />
            <div className={'flex flex-col'}>
              <span className={'text-sm text-content-secondary'}>{tr('PriceTable.line', 'Linka')}</span>
              <span className={'font-medium'}>{subjectName}</span>
            </div>
          </div>
        )}
        {infoText && <COInlineMessage variant={'info'}>{infoText}</COInlineMessage>}

        <div className={'grid grid-cols-[1fr_1fr_1fr_auto] gap-x-2 text-sm text-content-secondary'}>
          <span>{tr('PricePlan.activeLevel', 'Aktivní úroveň')}</span>
          <span>{tr('PricePlan.validFrom', 'Platí od')}</span>
          <span>{tr('PricePlan.validTo', 'Platí do')}</span>
          <span className={'w-8'} />
        </div>
        {intervals.map((interval, index) => {
          const error = submitted ? rowError(interval) : overlapping.has(interval.key) ? rowError(interval) : null;
          return (
            <div key={interval.key} className={'flex flex-col gap-1'} data-testid={`price-plan-row-${index}`}>
              <div className={'grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center'}>
                <Dropdown
                  value={interval.levelKey}
                  options={table.levels}
                  optionLabel={'name'}
                  optionValue={'key'}
                  placeholder={tr('PricePlan.selectLevel', 'Vyberte')}
                  onChange={e => update(interval.key, { levelKey: e.value })}
                  style={{ width: '100%' }}
                  data-testid={`price-plan-level-${index}`}
                />
                <RingilCalendar value={interval.validFrom} onChange={validFrom => update(interval.key, { validFrom })} fullWidth dataTestId={`price-plan-from-${index}`} />
                <RingilCalendar value={interval.validTo} onChange={validTo => update(interval.key, { validTo })} fullWidth dataTestId={`price-plan-to-${index}`} />
                <RingilButton
                  mode={'table'}
                  icon={<SvgDelete />}
                  tooltip={tr('PricePlan.removePeriod', 'Odebrat období')}
                  onClick={() => setIntervals(list => list.filter(i => i.key !== interval.key))}
                />
              </div>
              {error && <div className={'text-xs text-complementary-error'}>{error}</div>}
            </div>
          );
        })}
        <div>
          <RingilButton
            mode={'text-only'}
            icon={<SvgPlus className={'w-3 h-3'} />}
            label={tr('PricePlan.addPeriod', 'Přidat období')}
            onClick={() => setIntervals(list => [...list, newPriceInterval(table.defaultLevelKey ?? table.levels[0]?.key)])}
            dataTestId={'price-plan-add'}
          />
        </div>
      </div>
    </RingilSidebar>
  );
};

export default PriceLevelPlanSidebar;
